import { z } from 'zod';
import { costCategorySchema, costStatusSchema, equationKindSchema, equationScopeSchema, valuationStatusSchema, valuationTriggerSchema, } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
import { isoDateSchema, revisionSchema } from '../../common/schemas/money.schemas.js';
import { ownerSchema } from '../../common/schemas/owner.schemas.js';
import { paramUnitSchema } from '../../templates/schemas/templates.schemas.js';
import { blockingIssueSchema, missingInputSchema } from '../../estimates/schemas/estimates.schemas.js';
/**
 * `valuations.compute` runs the tenant's published policy over one record and hands back **everything
 * the estimator needs to explain the number**: the inputs with their provenance, what is still missing,
 * what is invalid, the outputs, the substituted trace and the resolved cost picture (plan §5.2, §5.4).
 *
 * `save`, `list` and `get` — the immutable snapshots — arrive with the offer step in phase 4. Compute
 * and save share one `ValuationsService.evaluate`, so the estimator and a stored snapshot can never
 * disagree (plan §6).
 */
export const provenanceSourceSchema = z.enum(['VEHICLE', 'TEMPLATE', 'OVERRIDE', 'SYSTEM', 'TRADE_IN', 'NOT_APPLICABLE']);
export const inputProvenanceSchema = z.object({
    source: provenanceSourceSchema,
    note: z.string().optional(),
});
/** One equation's line in the "why this price?" breakdown. */
export const traceEntrySchema = z.object({
    id: z.string(),
    label: z.string(),
    unit: paramUnitSchema,
    group: z.enum(['AGE_VALUE', 'INSPECTION', 'COSTS', 'ACQUISITION', 'TRADE_IN']),
    scope: equationScopeSchema,
    kind: equationKindSchema,
    expression: z.string(),
    /** The expression with every input and upstream equation replaced by its number. */
    substituted: z.string(),
    value: z.number().nullable(),
    skipped: z.boolean().optional(),
    cost: z
        .object({
        category: costCategorySchema,
        estimate: z.number(),
        resolved: z.number(),
        source: z.enum(['EQUATION', 'ITEMS', 'MANUAL']),
        provisional: z.boolean(),
    })
        .optional(),
    error: z.object({ code: z.string(), detail: z.string() }).optional(),
});
export const resolvedCostItemSchema = z.object({
    id: z.string(),
    name: z.string().optional(),
    category: costCategorySchema,
    status: costStatusSchema,
    amount: z.number(),
    basis: z.enum(['ESTIMATE', 'ACTUAL', 'ACTUAL_PLUS_REMAINING', 'WAIVED']),
    provisional: z.boolean(),
});
export const costResolutionSchema = z.object({
    category: costCategorySchema,
    amount: z.number(),
    items: z.array(resolvedCostItemSchema),
    provisional: z.boolean(),
});
export const computeValuationInputSchema = z.object({
    owner: ownerSchema,
});
export const computeValuationOutputSchema = z.object({
    owner: ownerSchema,
    inputs: z.record(z.string(), z.number()),
    provenance: z.record(z.string(), inputProvenanceSchema),
    missing: z.array(missingInputSchema),
    blocking: z.array(blockingIssueSchema),
    outputs: z.record(z.string(), z.number().nullable()),
    trace: z.array(traceEntrySchema),
    /** Empty categories keep the equation's own estimate (plan §5.4); cost items arrive in phase 4. */
    costResolution: z.array(costResolutionSchema),
    provisional: z.boolean(),
    noEconomicOffer: z.boolean(),
    issues: z.array(z.string()),
    fingerprint: z.string(),
    policyVersionId: uuidSchema,
    templateVersionId: uuidSchema.nullable(),
    valuationDate: isoDateSchema,
});
/**
 * An **immutable** snapshot (plan §4). It stores the two version ids, every input, every output, the
 * substituted trace and the cost resolution that produced it, so the figure a customer was quoted can be
 * reproduced years later even after the equations and the template values have moved on (PP AC14).
 *
 * `seq` is allocated under the owner's row lock and the previous SAVED row becomes `SUPERSEDED`, so a
 * record's snapshot sequence can never fork.
 */
export const valuationSnapshotSchema = z.object({
    id: uuidSchema,
    owner: ownerSchema,
    seq: z.number().int().min(1),
    trigger: valuationTriggerSchema,
    status: valuationStatusSchema,
    valuationDate: isoDateSchema,
    policyVersionId: uuidSchema,
    policyVersion: z.number().int().min(1).nullable(),
    templateVersionId: uuidSchema.nullable(),
    templateVersion: z.number().int().min(1).nullable(),
    templateName: z.string().nullable(),
    fingerprint: z.string(),
    /** True when the record has moved on since — the snapshot itself never changes (plan §5.2). */
    isStale: z.boolean(),
    appraisal: z.number().nullable(),
    ceiling: z.number().nullable(),
    requiredRetail: z.number().nullable(),
    projectedTotalCost: z.number().nullable(),
    provisional: z.boolean(),
    createdBy: uuidSchema.nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string().datetime(),
});
/** The whole stored document, for the read-only snapshot dialog. */
export const valuationSnapshotDetailSchema = valuationSnapshotSchema.extend({
    inputs: z.record(z.string(), z.number()),
    outputs: z.record(z.string(), z.number().nullable()),
    trace: z.array(traceEntrySchema),
    costResolution: z.array(costResolutionSchema),
});
export const saveValuationInputSchema = z.object({
    owner: ownerSchema,
    /** The owner's revision: a snapshot describes one version of one record (plan §2.3). */
    expectedRevision: revisionSchema,
});
export const listValuationsInputSchema = z.object({
    owner: ownerSchema,
});
export const listValuationsOutputSchema = z.object({
    owner: ownerSchema,
    items: z.array(valuationSnapshotSchema),
});
export const getValuationInputSchema = z.object({
    valuationId: uuidSchema,
});
//# sourceMappingURL=valuations.schemas.js.map